/**
 * Estimator defaults — one `estimator_settings` row (scope = 'default').
 * Staff-only by RLS. Money is stored in cents and exposed in dollars so the
 * wizard and the settings sliders share one unit.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db, describeDbError } from '../lib/db';

export const SETTINGS_SCOPE = 'default';

interface EstimatorSettingsRow {
  id: string;
  scope: string;
  labor_rate_cents: number | null;
  hardware_margin_pct: number | null;
  contingency_pct: number | null;
  default_valid_days: number | null;
  proposal_footer: string | null;
  updated_by_user_id: string | null;
  updated_at: string;
  updated_by: { full_name: string | null } | null;
}

const SETTINGS_COLUMNS =
  'id, scope, labor_rate_cents, hardware_margin_pct, contingency_pct, default_valid_days, proposal_footer, updated_by_user_id, updated_at, updated_by:users(full_name)';

export interface EstimatorDefaults {
  /** Present when a settings row exists for the scope. */
  exists: boolean;
  /** Labor rate in dollars per hour. */
  laborRate: number | null;
  hardwareMarginPct: number | null;
  contingencyPct: number | null;
  validDays: number | null;
  proposalFooter: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface EstimatorDefaultsInput {
  laborRate: number;
  hardwareMarginPct: number;
  contingencyPct: number;
  validDays: number;
  proposalFooter: string;
}

function toDefaults(row: EstimatorSettingsRow | null): EstimatorDefaults {
  if (!row) {
    return {
      exists: false,
      laborRate: null,
      hardwareMarginPct: null,
      contingencyPct: null,
      validDays: null,
      proposalFooter: null,
      updatedAt: null,
      updatedByName: null,
    };
  }
  return {
    exists: true,
    laborRate: row.labor_rate_cents === null ? null : row.labor_rate_cents / 100,
    hardwareMarginPct: row.hardware_margin_pct === null ? null : Number(row.hardware_margin_pct),
    contingencyPct: row.contingency_pct === null ? null : Number(row.contingency_pct),
    validDays: row.default_valid_days,
    proposalFooter: row.proposal_footer,
    updatedAt: row.updated_at,
    updatedByName: row.updated_by?.full_name ?? null,
  };
}

export const SETTINGS_QUERY_KEY = ['estimator-settings', SETTINGS_SCOPE] as const;

export function useEstimatorSettings() {
  return useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<EstimatorDefaults> => {
      const { data, error } = await db
        .from('estimator_settings')
        .select(SETTINGS_COLUMNS)
        .eq('scope', SETTINGS_SCOPE)
        .maybeSingle();
      if (error) throw new Error(describeDbError(error, 'Could not load estimator settings.'));
      return toDefaults((data as EstimatorSettingsRow | null) ?? null);
    },
    staleTime: 60 * 1000,
  });
}

export function useSaveEstimatorSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EstimatorDefaultsInput): Promise<EstimatorDefaults> => {
      const payload = {
        scope: SETTINGS_SCOPE,
        labor_rate_cents: Math.round(input.laborRate * 100),
        hardware_margin_pct: input.hardwareMarginPct,
        contingency_pct: input.contingencyPct,
        default_valid_days: input.validDays,
        proposal_footer: input.proposalFooter.trim() || null,
      };
      const { data, error } = await db
        .from('estimator_settings')
        .upsert(payload, { onConflict: 'scope' })
        .select(SETTINGS_COLUMNS)
        .single();
      if (error) throw new Error(describeDbError(error, 'Could not save estimator settings.'));
      return toDefaults(data as EstimatorSettingsRow);
    },
    onSuccess: (defaults) => {
      qc.setQueryData(SETTINGS_QUERY_KEY, defaults);
    },
  });
}
